import DisplayObject = PIXI.DisplayObject;
import ScreenShakeComponent from "slotworks/components/effects/screen-shake-component";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import { Orientation } from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import Contract from "slotworks/utils/contracts/contract";
import Sequence from "slotworks/utils/contracts/sequence";
import { Easing, Tween } from "slotworks/utils/tween";

class ReelDropComponent {

    private currentY: { y: number } = {y: 0};

    private dropDisplayObjects: DropDisplayObject[];

    private dropElements: DropElement[];

    public init(dropElements: DropElement[]) {
        this.dropElements = dropElements;

        CanvasManager.onOrientationChange.add((orientation: Orientation) => this.updateOrientation(orientation));
    }

    public forceOriginalPosition(): Contract<void> {
        return new Contract((resolve) => {
            this.getDropDisplayObjects().forEach((element) => {
                element.setVisible(true);
                this.currentY.y = 0;
                element.setY(0);
            });
            resolve();
        });
    }

    public transitionOut() {
        return new Contract<void>((resolve) => {

            ScreenShakeComponent.shakeScreen().then(() => {
                this.resetDropElements();

                Layers.SmartButton.hide();

                this.slide(true, Easing.Quadratic.In).execute();
                resolve(null);
            });
        });
    }

    public transitionIn() {
        return new Sequence([
            () => Layers.MatrixForeground.setScene("freespins"),
            () => Contract.wrap(() => {
                const borderSprite = Layers.MatrixForeground.getSprite("border");
                if (borderSprite) {
                    this.dropDisplayObjects.push(new DropDisplayObject(borderSprite));
                }
            }),
            () => this.slide(false, Easing.Quadratic.Out),
            () => Contract.wrap(() => {
                Layers.SmartButton.show();
            })
        ]);
    }

    protected slide(out: boolean, easing: (k: number) => number) {
        return new Contract<void>((resolve) => {

            const elements = this.getDropDisplayObjects();
            const to: number = out ? CanvasManager.stageHeight : 0;

            const tween = new Tween(this.currentY);
            tween.to({y: to}, 500);
            tween.easing(easing);
            tween.onStart(() => {
                elements.forEach((element: DropDisplayObject) => {
                    element.displayObject.cacheAsBitmap = true;
                    element.setVisible(true);
                });
            });
            tween.onUpdate(() => {
                elements.forEach((element: DropDisplayObject) => {
                    element.setY(this.currentY.y);
                });
            });
            tween.onComplete(() => {
                elements.forEach((element: DropDisplayObject) => {
                    element.setVisible(!out);
                    element.displayObject.cacheAsBitmap = false;
                });
                resolve(null);
            });
            tween.start();
        });
    }

    private resetDropElements() {
        this.getDropDisplayObjects().forEach((element: DropDisplayObject) => {
            element.destroy();
        });
        this.dropDisplayObjects = null;
    }

    private getDropDisplayObjects() {
        if (!this.dropDisplayObjects) {
            this.dropDisplayObjects = [
                new DropDisplayObject(Layers.MatrixContent.container)
            ];

            this.dropElements.forEach((element: DropElement) => {
                const displayObject: DisplayObject = element.spriteName ?
                    element.layer.getSprite(element.spriteName) :
                    element.layer.container;
                this.dropDisplayObjects.push(new DropDisplayObject(displayObject));
            });
        }

        return this.dropDisplayObjects;
    }

    private updateOrientation(orientation: Orientation) {
        this.getDropDisplayObjects().forEach((element: DropDisplayObject) => {
            element.resetOriginalPosition();
            element.setY(this.currentY.y);
        });
    }
}

export default new ReelDropComponent();

export class DropDisplayObject {
    public originalY: number;

    private originalVisible: boolean;

    constructor(public displayObject: DisplayObject) {
        this.resetOriginalPosition();

        this.originalVisible = displayObject.visible;
    }

    public setY(y: number) {
        if (this.displayObject.transform) {
            this.displayObject.y = this.originalY + y;
        }

    }

    public setVisible(visible: boolean) {
        this.displayObject.visible = this.originalVisible && visible;
    }

    public resetOriginalPosition() {
        if (this.displayObject.transform) {
            this.originalY = this.displayObject.y;
        }
    }

    public destroy() {
        this.displayObject = null;
        this.originalY = 0;
    }
}

export interface DropElement {
    layer: Layers;
    spriteName?: string;
}
